import { Button } from '@/components/ui/button'
import type { MagicLinkErrorReason } from '@/lib/magic-link-error'
import { isMagicLinkErrorReason } from '@/lib/magic-link-error'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircle } from 'lucide-react'

interface MagicLinkErrorSearch {
  reason: MagicLinkErrorReason
}

interface ReasonCopy {
  title: string
  description: string
  showRequestNew: boolean
}

const REASON_COPY: Record<MagicLinkErrorReason, ReasonCopy> = {
  used: {
    title: 'This magic link has already been used',
    description: 'For your security, magic links can only be used once. Request a new one to sign in.',
    showRequestNew: true,
  },
  expired: {
    title: 'This magic link has expired',
    description: 'Magic links are valid for 10 minutes. Request a new one to sign in.',
    showRequestNew: true,
  },
  invalid: {
    title: 'This magic link is invalid',
    description: 'The link you used isn\u2019t valid. Request a new one to sign in.',
    showRequestNew: true,
  },
  no_account: {
    title: 'No account found',
    description: 'We couldn\u2019t find an account for this email. Please contact your administrator if you think this is a mistake.',
    showRequestNew: false,
  },
  unknown: {
    title: 'We couldn\u2019t sign you in',
    description: 'Something went wrong with that magic link. Request a new one or try again later.',
    showRequestNew: true,
  },
}

export const Route = createFileRoute('/_login_forms/_magic_link/magic-link-error')({
  validateSearch: (search: Record<string, unknown>): MagicLinkErrorSearch => {
    const raw = search['reason'] // eslint-disable-line dot-notation
    return { reason: isMagicLinkErrorReason(raw) ? raw : 'unknown' }
  },
  beforeLoad: () => ({
    title: 'Sign in link unavailable',
    description: 'There was a problem with your magic link.',
  }),
  component: MagicLinkErrorPage,
})

function MagicLinkErrorPage() {
  const { reason } = Route.useSearch()
  const copy = REASON_COPY[reason]

  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="h-6 w-6 text-destructive" />
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">{copy.title}</h3>
        <p className="text-sm text-muted-foreground">{copy.description}</p>
      </div>
      <div className="space-y-2">
        {copy.showRequestNew && (
          <Button asChild variant="default" className="w-full">
            <Link to="/magic-link">Request a new magic link</Link>
          </Button>
        )}
        <Button asChild variant="outline" className="w-full">
          <Link to="/login">Back to login</Link>
        </Button>
      </div>
    </div>
  )
}
